import AddCommentIcon from "@mui/icons-material/AddComment";
import SendIcon from "@mui/icons-material/Send";
import React, {
  FormEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
// import { v4 as uuidv4 } from "uuid";
import apiClient, { basePath } from "../../utils/api";
import type { ChatMessage } from "./ChatMessages";
import "./styles.css"; // Assuming you've ported CSS to be imported in React

import { fetchEventSource } from "@microsoft/fetch-event-source";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { CodeInterpreter } from "../CodeInterpreter/CodeInterpreter";
import { ChatContext } from "./ChatContainer";
import ChatField from "./ChatField";
import ChatMessages from "./ChatMessages";
import TabPanel from "./ChatTabPanel";
import { useSaveInteraction } from "../../utils/useSaveInteraction";
import { useAuth } from "../../auth/AuthContext";
import { featureFlagChecker } from "../../utils/featureFlagChecker";

export const IDEContext = React.createContext(
  {} as {
    IDECode: string;
    setIDECode: (code: string) => void;
    IDEOutputCode: string;
    setIDEOutputCode: (code: string) => void;
    IDEfileNameState: string[];
    setIDEFileNameState: React.Dispatch<React.SetStateAction<string[]>>;
    IDEFileBufferState: ArrayBuffer[];
    setIDEFileBufferState: React.Dispatch<React.SetStateAction<ArrayBuffer[]>>;
    IDEModifiedFileBufferState: ArrayBuffer[];
    setModifiedFileBufferState: React.Dispatch<
      React.SetStateAction<ArrayBuffer[]>
    >;
    IDETabs: { label: string; content: string }[];
    setIDETabs: React.Dispatch<
      React.SetStateAction<{ label: string; content: string }[]>
    >;
    IDETabvalue: number;
    setIDETabValue: React.Dispatch<React.SetStateAction<number>>;
  }
);

export const Chat: React.FC = () => {
  const { saveInteraction } = useSaveInteraction();
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState<string>("");
  const [botAnswering, setBotAnswering] = useState<boolean>(false);

  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // tabs
  const [tabIndex, setTabIndex] = useState<number>(0); // TODO: change back 0

  // class
  const {
    class: selectedClass,
    currentPDFID: selectedPDFID,
    currentPDFPage: selectedPDFPage,
    currentPDFMaterialType: selectedPDFMaterialType,
    engineChoice,
    setEngineChoice,
  } = useContext(ChatContext);

  // Check Available Features
  const { availableFeatures } = useAuth();
  // Check if the user has the feature to use Qwen
  const qwenEnabled = featureFlagChecker({
    featureFlag: "QwenModel",
    availableFeatures: availableFeatures,
    classId: selectedClass?.classID,
  });

  // IDE
  const [IDECode, setIDECode] = useState<string>("");
  const [IDEOuputCode, setIDEOutputCode] = useState<string>("");
  const [IDEfileNameState, setIDEFileNameState] = useState<string[]>([]);
  const [IDEFileBufferState, setIDEFileBufferState] = useState<ArrayBuffer[]>(
    []
  );
  const [IDEModifiedFileBufferState, setModifiedFileBufferState] = useState<
    ArrayBuffer[]
  >([]);
  const [IDEtabs, setIDETabs] = React.useState([
    { label: "Code Space", content: "Code space" },
  ]);
  const [IDETabvalue, setIDETabValue] = React.useState(0);

  const loaderIdRef = useRef("");

  useEffect(() => {
    const handleBeforeUnload = () => {
      navigator.sendBeacon(`${basePath}/student/beacon`);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }); // Only re-run the effect if elapsedTime change

  // save the lastest dialog between user and assistent as interaction
  useEffect(() => {
    const lastPrompt = chatMessages[chatMessages.length - 2];
    const lastMessage = chatMessages[chatMessages.length - 1];
    if (
      lastMessage &&
      typeof lastMessage.content !== "undefined" &&
      lastPrompt &&
      lastMessage.content !== "..." &&
      lastMessage.streaming === "stop"
    ) {
      console.log("The new prompt content:", lastPrompt.content);
      console.log("The new response content:", lastMessage.content);

      saveInteraction({
        name: "frontend-interaction-chat",
        context: {
          uniqueID: lastMessage.uniqueId,
          content: {
            prompt: lastPrompt.content,
            answer: lastMessage.content,
          },
          pdfID: selectedPDFID,
          pdfPage: selectedPDFPage,
          pdfMaterialType: selectedPDFMaterialType,
          engineChoice: engineChoice,
        },
      });
    }
  }, [
    chatMessages,
    selectedPDFID,
    selectedPDFMaterialType,
    saveInteraction,
    selectedPDFPage,
    engineChoice,
  ]);

  const handleSendRetryMessage = async (message: ChatMessage) => {
    // remove last two messages from chatmessages
    console.log("retrying message", message);
    // find the index of message that needs to be retried, remove it and previous message
    const index = chatMessages.findIndex(
      (msg) => msg.uniqueId === message.uniqueId
    );
    console.log("index", index);
    // find the element before the message that needs to be retried
    const previousElement = chatMessages[index - 1];
    if (index === -1 || !previousElement) return;
    setChatMessages((prevMessages) =>
      prevMessages.filter((_, i) => i !== index && i !== index - 1)
    );
    console.log("retrying message", previousElement.content);
    handleSendMessage(previousElement.content);
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const makeLLMAPICall = (promptMessage: string, chatSummary: string) => {
    // change this API call based on the student's current prompt and the retrieved docs from RAG
    console.log("my frontend prompt and summary", promptMessage, chatSummary);
    console.log("Sending page number", (selectedPDFPage || 0) + 1);

    // start SSE here
    startChat({
      prompt: promptMessage,
      chatSummary: chatSummary,
      studentContext: {
        classId: selectedClass?.classID,
        pdfId: selectedPDFID,
        pageNumber: (selectedPDFPage || 0) + 1,
        materialType: selectedPDFMaterialType,
      },
      engineChoice: engineChoice || "openai",
    });
  };

  const startChat = (payload: any) => {
    setBotAnswering(true);
    const url = `${basePath}/student/chat`;
    const token = localStorage.getItem("authToken");

    if (!token) {
      setSnackbarOpen(true);
      setSnackbarMessage("No token found, please login again");
      return;
    }
    return new Promise((resolve, reject) => {
      const makeRequest = (token: string) => {
        fetchEventSource(url, {
          method: "POST",
          headers: {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          openWhenHidden: true,
          body: JSON.stringify(payload),
          credentials: "include",
          onopen(response) {
            return new Promise((resolve, reject) => {
              if (response.ok && response.status === 200) {
                console.log("Connection opened!");
                setSnackbarOpen(true);
                setSnackbarMessage("Connected to Kiwi");
                resolve();
              } else if (response.status === 401) {
                // Try to get the key again via refresh token
                setSnackbarOpen(true);
                setSnackbarMessage("Token expired, refreshing...");
                apiClient
                  .authControllerRefreshAccessToken()
                  .then((accessTokenResponse) => {
                    if (accessTokenResponse.accessToken) {
                      localStorage.setItem(
                        "authToken",
                        accessTokenResponse.accessToken
                      );
                      setSnackbarMessage("Token refreshed, retrying...");
                      makeRequest(accessTokenResponse.accessToken); // Retry with the new token
                      resolve();
                    }
                    reject(new Error("Failed to refresh token"));
                  });
              } else if (
                response.status >= 400 &&
                response.status < 500 &&
                response.status !== 429 &&
                response.status !== 403
              ) {
                setSnackbarOpen(true);
                console.log("Client side error, stop the request");
                setSnackbarMessage(`An error occurred: ${response.statusText}`);
                reject(new Error("Client side error, stop the request"));
              } else {
                if (response.status === 429) {
                  setSnackbarOpen(true);
                  setSnackbarMessage("You are out of tokens.");
                }
                if (response.status === 403) {
                  setSnackbarOpen(true);
                  setSnackbarMessage(
                    "You are not allowed to use this feature."
                  );
                }
                resolve(); // resolve even if there's no specific action to take
              }
            });
          },
          onmessage(event) {
            console.log("event", event);
            try {
              if (event.data) {
                const parsedData = JSON.parse(event.data);
                const { botResponse, streaming, citations, id } =
                  parsedData.bot;
                setChatMessages((prevMessages) => {
                  const lastMessage = prevMessages[prevMessages.length - 1];
                  // console.log("lastMessage", lastMessage);
                  // check if lastMessage is the loader,
                  // change the response of the last message
                  if (
                    lastMessage.uniqueId === loaderIdRef.current ||
                    lastMessage.uniqueId === id
                  ) {
                    lastMessage.uniqueId = id;
                    lastMessage.content = botResponse;
                    lastMessage.streaming = streaming;
                    lastMessage.typing = false;
                    lastMessage.citations = citations;
                    return [...prevMessages];
                  } else {
                    return [
                      ...prevMessages,
                      {
                        isAi: true,
                        content: botResponse,
                        streaming: streaming,
                        error: false,
                      },
                    ];
                  }
                });

                // // check if the streaming == 'stop' -> show feedback area
                // if (lastMessage.isAi && lastMessage.streaming !== "stop") {
                //     lastMessage.content = botResponse;
                //     lastMessage.streaming = streaming;
                //     return [...prevMessages];
                // } else {
                //     return [
                //         ...prevMessages,
                //         { isAi: true, content: botResponse, streaming: streaming, error: false}
                //     ];
                // }

                if (streaming === "stop") {
                  setBotAnswering(false);
                }
              }
            } catch (error) {
              console.error("Error parsing JSON:", error);
            }
          },
          onerror(err) {
            console.log("HERE");
            setSnackbarOpen(true);
            setSnackbarMessage("Connection lost. Reconnecting...");
            console.error("Error:", err);
            console.error("Error message", err.message);
            setBotAnswering(false);
            if (err.code === 429) throw new Error("You are out of tokens.");
            if (err.code === 403)
              throw new Error("You are not allowed to use this feature.");
          },
          onclose() {
            console.log("Connection closed by the server");
            setBotAnswering(false);
          },
        });
      };

      try {
        makeRequest(token);
      } catch (error: any) {
        console.log("error at catch", error);
        setSnackbarOpen(true);
        setSnackbarMessage(error.message);
        setBotAnswering(false);
      }
    });
  };

  interface SummaryRequest {
    role: string;
    content: string;
  }
  // Handle form submission
  const handleSendMessage = async (promptMessage: string) => {
    const newMessage: ChatMessage = {
      isAi: false,
      content: promptMessage,
      error: false,
    };
    setChatMessages((prevMessages) => [...prevMessages, newMessage]);
    // eslint-disable-next-line no-useless-concat
    console.log("student prompt", promptMessage);
    setPrompt("");
    setBotAnswering(true);

    // Simulate the loader
    const loaderId = `loading-${new Date().getTime()}`;
    const loaderMessage: ChatMessage = {
      isAi: true,
      content: "...",
      typing: true,
      uniqueId: loaderId,
      imgURLs: [],
      error: false,
      streaming: "null",
    };
    loaderIdRef.current = loaderId;
    setChatMessages((prevMessages) => [...prevMessages, loaderMessage]);

    console.log("chat messages", chatMessages);
    const summaryProps: SummaryRequest[] = chatMessages.map((msg) => {
      if (msg.isAi) {
        return { role: "assistant", content: msg.content };
      } else {
        return { role: "user", content: msg.content };
      }
    });
    console.log("summaryProps", summaryProps);

    let summary = "";
    try {
      if (summaryProps.length >= 2) {
        const summaryResponse = await apiClient.studentControllerSummary({
          studentSummaryRequest: {
            conversation: summaryProps,
            studentContext: {
              classId: selectedClass?.classID || "",
              pdfId: selectedPDFID || "",
              pageNumber: `${(selectedPDFPage || 0) + 1}`,
              materialType: selectedPDFMaterialType || "",
            },
            engineChoice: engineChoice || "openai",
          },
        });
        summary = summaryResponse.summary;
      }
      console.log("chat length", chatMessages.length);
    } catch (error: any) {
      console.error("Error getting summary:", error);
      setSnackbarOpen(true);
      if (error.status === 429) {
        setSnackbarMessage("You are out of tokens.");
      }
    }

    makeLLMAPICall(promptMessage, summary);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSendMessage(prompt);
  };

  const exportCode = (prompt: string) => {
    setPrompt(prompt);
    setTabIndex(0);
  };

  const importCode = (code: string) => {
    setIDECode(code);
    setTabIndex(1);
  };

  const handleNewChat = () => {
    setChatMessages([]);
  };

  return (
    <Box height="calc(100vh - 60px)" overflow="auto">
      <Stack height="100%">
        <Tabs
          value={tabIndex}
          onChange={(e: React.SyntheticEvent, newValue: number) =>
            setTabIndex(newValue)
          }
          centered
        >
          <Tab label="Chat" value={0} />
          <Tab label="IDE" value={1} />
          {/* <Tab label="Chat" value={2} /> */}
        </Tabs>
        <TabPanel value={tabIndex} index={0}>
          <Stack
            overflow="hidden"
            useFlexGap
            direction="column"
            justifyContent="space-between"
            height="100%"
          >
            <Box overflow="auto">
              <ChatMessages
                chatMessages={chatMessages}
                handleSendRetryMessage={handleSendRetryMessage}
                messageEndRef={messageEndRef}
                importCode={importCode}
              />
            </Box>
            <Box padding={1}>
              <form ref={formRef} onSubmit={handleSubmit}>
                <Grid container columnSpacing={1} columns={16} direction="row">
                  {qwenEnabled && (
                    <Grid
                      item
                      xs={16}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <FormControl>
                        <InputLabel id="model-select-label">Model</InputLabel>
                        <Select
                          labelId="model-select-label"
                          id="model-select"
                          value={engineChoice}
                          label="Model"
                          onChange={(e) => {
                            if (e.target.value) {
                              localStorage.setItem(
                                "engineChoice",
                                e.target.value
                              );
                              if (setEngineChoice)
                                setEngineChoice(e.target.value);
                            }
                          }}
                        >
                          <MenuItem value={"openai"}>ChatGPT</MenuItem>
                          <MenuItem value={"qwen"}>Qwen</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={15} sm={13} md={14}>
                    <ChatField
                      botAnswering={botAnswering}
                      setPrompt={setPrompt}
                      prompt={prompt}
                      formRef={formRef}
                    />
                  </Grid>
                  <Grid
                    item
                    sm={3}
                    md={2}
                    sx={{
                      display: "inline-flex",
                      flexDirection: "column",
                    }}
                  >
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={botAnswering}
                      size="small"
                      sx={{
                        height: "50%",
                      }}
                    >
                      <SendIcon />
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={botAnswering}
                      onClick={handleNewChat}
                      sx={{
                        height: "50%",
                      }}
                      size="small"
                    >
                      <Tooltip title="Start New Conversation">
                        <AddCommentIcon />
                      </Tooltip>
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </Stack>
        </TabPanel>
        <TabPanel value={tabIndex} index={1}>
          <IDEContext.Provider
            value={{
              IDECode,
              setIDECode,
              IDEOutputCode: IDEOuputCode,
              setIDEOutputCode,
              IDEfileNameState: IDEfileNameState,
              setIDEFileNameState,
              IDEFileBufferState: IDEFileBufferState,
              setIDEFileBufferState,
              IDEModifiedFileBufferState: IDEModifiedFileBufferState,
              setModifiedFileBufferState,
              IDETabs: IDEtabs,
              setIDETabs,
              IDETabvalue,
              setIDETabValue,
            }}
          >
            <CodeInterpreter
              pdfInfo={selectedPDFID || ""}
              handleSendMessage={handleSendMessage}
              Messages={chatMessages}
              exportCode={exportCode}
            />
          </IDEContext.Provider>
        </TabPanel>
        {/* <TabPanel value={tabIndex} index={2}>
        2
      </TabPanel> */}
      </Stack>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};
